import {
  ATTACHMENT_KINDS,
  type AttachmentKind,
  type BasePriceSource,
  datePl,
  CATALOG_KINDS,
  type CatalogItemDto,
  catalogItemInputSchema,
  type CatalogKind,
  type CatalogPriceDto,
  formatMoney,
  marginPctOf,
  markupPctOf,
  type MeDto,
  PRICE_ROUNDINGS,
  type PriceRounding,
  ruleSalePrice,
  salePriceForMargin,
  unitGrossGr,
  unitNetFromGrossGr,
  UNITS,
  VAT_RATES,
} from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, Card, Checkbox, Divider, Drawer, FileButton, Group, MultiSelect, NumberInput, Select, SimpleGrid, Stack, Switch, Table, Text, Textarea, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconHistory, IconPackage, IconPaperclip, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText } from '../../api';
import { can, useMe } from '../../auth';
import { ask } from '../../confirm';
import { TradeBadge } from '../../components/Badges';
import { Money, MoneyInput } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { useCatalogGroups, useTrades, useWarehouseSettings } from '../../data';
import { formatDateTime, pctPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';

type PackForm = { id: string | null; name: string; factor: number; ean: string; active: boolean };

type Form = {
  kind: CatalogKind;
  tradeCodes: string[];
  groupId: string | null;
  ean: string;
  stocked: boolean;
  hideInOffers: boolean;
  leadTimeDays: number | null;
  packs: PackForm[];
  code: string;
  name: string;
  description: string;
  manufacturer: string;
  model: string;
  unit: string;
  vatRate: number;
  salePriceGr: number | null;
  purchasePriceGr: number | null;
  forInstallation: boolean;
  servicePrice: boolean;
  active: boolean;
  components: { itemId: string; quantity: number }[];
  targetMarginPct: number | null;
  priceRounding: PriceRounding;
};

const empty: Form = {
  kind: 'urzadzenie',
  tradeCodes: [],
  groupId: null,
  ean: '',
  stocked: true,
  hideInOffers: false,
  leadTimeDays: null,
  packs: [],
  code: '',
  name: '',
  description: '',
  manufacturer: '',
  model: '',
  unit: 'szt.',
  vatRate: 8,
  salePriceGr: null,
  purchasePriceGr: null,
  forInstallation: true,
  servicePrice: false,
  active: true,
  components: [],
  targetMarginPct: null,
  priceRounding: 'brak',
};

function ItemEditor({ item, all, onDone, onCancel }: { item: CatalogItemDto | null; all: CatalogItemDto[]; onDone: (i: CatalogItemDto) => void; onCancel: () => void }) {
  // pola magazynowe (stan, opakowania, czas dostawy, materiał drobny) — tylko z włączonym modułem magazynu
  const wh = !!useMe().data?.features.magazyn;
  const trades = useTrades();
  const groups = useCatalogGroups(wh);
  const [f, setF] = useState<Form>(
    item
      ? {
          kind: item.kind,
          tradeCodes: item.tradeCodes,
          groupId: item.groupId,
          ean: item.ean,
          stocked: item.stocked,
          hideInOffers: item.hideInOffers,
          leadTimeDays: item.leadTimeDays,
          packs: item.packs.map((p) => ({ ...p })),
          code: item.code,
          name: item.name,
          description: item.description,
          manufacturer: item.manufacturer,
          model: item.model,
          unit: item.unit,
          vatRate: item.vatRate,
          salePriceGr: item.salePriceGr,
          purchasePriceGr: item.purchasePriceGr,
          forInstallation: item.forInstallation,
          servicePrice: item.servicePrice,
          active: item.active,
          components: item.components.map((c) => ({ itemId: c.itemId, quantity: c.quantity })),
          targetMarginPct: item.targetMarginPct,
          priceRounding: item.priceRounding,
        }
      : empty,
  );
  const stockable = f.kind === 'urzadzenie' || f.kind === 'material';
  const [errors, setErrors] = useState<string[]>([]);
  // marża liczona od ceny sprzedaży (jak w ofertach i zestawieniach); przy edycji — reguła albo marża z zapisanych cen
  const [margin, setMargin] = useState<number | null>(() => item?.targetMarginPct ?? marginPctOf(item?.salePriceGr ?? null, item?.purchasePriceGr ?? null));
  // stała marża: zapisana w pozycji, cena sprzedaży liczona z ceny zakupu (także przy każdej późniejszej zmianie)
  const [fixed, setFixed] = useState(item?.targetMarginPct != null);
  const costVisible = item ? item.costVisible : (all[0]?.costVisible ?? true);
  const markup = markupPctOf(f.salePriceGr, f.purchasePriceGr);
  const priceFor = (purchaseGr: number, m: number, isFixed = fixed, rounding = f.priceRounding) => (isFixed ? ruleSalePrice(purchaseGr, m, rounding) : salePriceForMargin(purchaseGr, m));
  const qc = useQueryClient();
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: (body: unknown) => (item ? api<CatalogItemDto>(`/catalog/${item.id}`, { method: 'PUT', body }) : api<CatalogItemDto>('/catalog', { method: 'POST', body })),
    onSuccess: (saved) => {
      notifyOk(item ? 'Zapisano pozycję katalogu' : 'Dodano pozycję katalogu');
      void qc.invalidateQueries({ queryKey: ['catalog'] });
      onDone(saved);
    },
    onError: (e) => setErrors([errorText(e)]),
  });
  const submit = () => {
    if (fixed && (margin === null || f.purchasePriceGr === null)) return setErrors(['Stała marża: podaj cenę zakupu i marżę']);
    if (f.packs.some((p) => !p.name.trim())) return setErrors(['Podaj nazwę każdego opakowania']);
    const r = catalogItemInputSchema.safeParse({ ...f, stocked: stockable ? f.stocked : false, packs: stockable ? f.packs : [], targetMarginPct: fixed ? margin : null });
    if (!r.success) return setErrors([...new Set(r.error.issues.map((i) => i.message))]);
    setErrors([]);
    save.mutate(r.data);
  };
  const componentOptions = all.filter((i) => i.kind !== 'pakiet' && i.id !== item?.id).map((i) => ({ value: i.id, label: `${i.name}${i.code ? ` (${i.code})` : ''}` }));
  const componentsSum = f.components.reduce((s, c) => s + (all.find((i) => i.id === c.itemId)?.salePriceGr ?? 0) * c.quantity, 0);

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select
          label="Rodzaj"
          data={Object.entries(CATALOG_KINDS).map(([value, label]) => ({ value, label }))}
          value={f.kind}
          onChange={(v) => {
            if (!v) return;
            const k = v as CatalogKind;
            // nowy materiał: domyślnie ukryty w wyborze pozycji oferty (materiały drobne — docs/14 decyzja 11)
            setF((x) => ({ ...x, kind: k, stocked: k === 'urzadzenie' || k === 'material', ...(item ? {} : { hideInOffers: wh && k === 'material' }) }));
          }}
          allowDeselect={false}
          disabled={!!item && (item.kind === 'pakiet' || f.kind === 'pakiet')}
        />
        {/* grupy, EAN, jednostka magazynowa, cena bazowa — część modułu magazynu (bez niego katalog jak przed modułem) */}
        {wh && <Select label="Grupa" placeholder="bez grupy" data={(groups.data ?? []).filter((g) => g.active || g.id === f.groupId).map((g) => ({ value: g.id, label: g.name }))} value={f.groupId} onChange={(v) => set('groupId', v)} clearable searchable />}
      </SimpleGrid>
      <MultiSelect
        label="Branże"
        placeholder={f.tradeCodes.length ? undefined : 'wspólny dla wszystkich branż'}
        description="Jeden produkt może służyć kilku branżom — nie twórz kopii."
        data={(trades.data ?? []).map((t) => ({ value: t.code, label: t.name }))}
        value={f.tradeCodes}
        onChange={(v) => set('tradeCodes', v)}
        clearable
      />
      <TextInput label="Nazwa" value={f.name} onChange={(e) => set('name', e.currentTarget.value)} />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput label="Indeks" value={f.code} onChange={(e) => set('code', e.currentTarget.value)} />
        {wh && <TextInput label="EAN" value={f.ean} onChange={(e) => set('ean', e.currentTarget.value)} />}
        <TextInput label="Producent" value={f.manufacturer} onChange={(e) => set('manufacturer', e.currentTarget.value)} />
        <TextInput label="Model" value={f.model} onChange={(e) => set('model', e.currentTarget.value)} />
      </SimpleGrid>
      <Textarea label="Opis (trafia do oferty)" autosize minRows={2} value={f.description} onChange={(e) => set('description', e.currentTarget.value)} />
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select label={stockable && wh ? 'Jednostka magazynowa' : 'Jednostka'} data={[...UNITS]} value={f.unit} onChange={(v) => v && set('unit', v)} allowDeselect={false} />
        <Select label="VAT" data={VAT_RATES.map((r) => ({ value: String(r), label: pctPl(r) }))} value={String(f.vatRate)} onChange={(v) => v && set('vatRate', Number(v))} allowDeselect={false} />
      </SimpleGrid>
      {stockable && wh && (
        <Stack gap={6}>
          <Group gap="lg" wrap="wrap" align="flex-end">
            <Switch label="Prowadź stan magazynowy" checked={f.stocked} onChange={(e) => set('stocked', e.currentTarget.checked)} />
            <Switch label="Ukryj w wyborze pozycji oferty (materiał drobny)" checked={f.hideInOffers} onChange={(e) => set('hideInOffers', e.currentTarget.checked)} />
            <NumberInput
              label="Czas dostawy (dni)"
              w={{ base: '100%', sm: 150 }}
              min={0}
              max={365}
              allowDecimal={false}
              value={f.leadTimeDays ?? ''}
              onChange={(v) => set('leadTimeDays', v === '' || v === null ? null : Number(v))}
            />
          </Group>
          <Text fw={600} size="sm" mt="xs">
            Opakowania zakupowe
          </Text>
          <Text size="xs" c="dimmed">
            Zakup w opakowaniach (np. krążek 100 m) — stan zawsze w jednostce magazynowej ({f.unit}).
          </Text>
          {f.packs.map((p, i) => (
            <Group key={p.id ?? `n${i}`} wrap="nowrap" align="flex-end" style={{ opacity: p.active ? 1 : 0.5 }}>
              <TextInput placeholder="np. Krążek 100 m" value={p.name} onChange={(e) => set('packs', f.packs.map((x, j) => (j === i ? { ...x, name: e.currentTarget.value } : x)))} style={{ flex: 1 }} />
              <NumberInput
                w={150}
                min={0.001}
                decimalScale={3}
                decimalSeparator=","
                suffix={` ${f.unit}`}
                value={p.factor}
                onChange={(v) => set('packs', f.packs.map((x, j) => (j === i ? { ...x, factor: Number(v) || 1 } : x)))}
                aria-label="Przelicznik"
              />
              <TextInput w={150} placeholder="EAN" value={p.ean} onChange={(e) => set('packs', f.packs.map((x, j) => (j === i ? { ...x, ean: e.currentTarget.value } : x)))} />
              <ActionIcon variant="subtle" color="red" onClick={() => set('packs', p.id ? f.packs.map((x, j) => (j === i ? { ...x, active: !x.active } : x)) : f.packs.filter((_, j) => j !== i))} aria-label={p.active ? 'Wyłącz opakowanie' : 'Włącz opakowanie'}>
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
          <Group>
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => set('packs', [...f.packs, { id: null, name: '', factor: 1, ean: '', active: true }])}>
              Dodaj opakowanie
            </Button>
          </Group>
        </Stack>
      )}
      {wh && item?.basePrice && <BasePriceInfo item={item} />}
      {/* cena zakupu + pożądana marża → cena sprzedaży; zmiana ceny sprzedaży przelicza marżę (bez stałej marży) */}
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
        <MoneyInput
          label={wh ? 'Ręczna cena bazowa (zakup netto)' : 'Cena zakupu netto'}
          valueGr={f.purchasePriceGr}
          onChangeGr={(v) => {
            const sale = v !== null && margin !== null ? priceFor(v, margin) : null;
            setF((x) => ({ ...x, purchasePriceGr: v, ...(sale !== null ? { salePriceGr: sale } : {}) }));
          }}
        />
        {costVisible ? <NumberInput
          label="Marża"
          suffix="%"
          decimalScale={1}
          decimalSeparator=","
          min={-99}
          max={99.9}
          clampBehavior="strict"
          placeholder="np. 25%"
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          value={margin ?? ''}
          onChange={(v) => {
            const m = v === '' || v === null ? null : Number(v);
            setMargin(m);
            const sale = m !== null && f.purchasePriceGr !== null ? priceFor(f.purchasePriceGr, m) : null;
            if (sale !== null) set('salePriceGr', sale);
          }}
          description={f.purchasePriceGr === null ? 'Wpisz cenę zakupu, a cena sprzedaży wyliczy się sama' : markup !== null ? `narzut ${pctPl(markup)} · zysk ${formatMoney((f.salePriceGr ?? 0) - f.purchasePriceGr)}` : 'od ceny sprzedaży'}
        /> : <div />}
        <MoneyInput
          label="Cena sprzedaży netto"
          valueGr={f.salePriceGr}
          disabled={fixed}
          description={fixed ? 'liczona ze stałej marży' : undefined}
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          onChangeGr={(v) => {
            set('salePriceGr', v);
            setMargin(marginPctOf(v, f.purchasePriceGr));
          }}
        />
        {/* katalog trzyma netto; brutto przy stawce pozycji — do wpisania i podglądu (w ofercie liczy się od stawki oferty) */}
        <MoneyInput
          label={`Cena sprzedaży brutto (VAT ${f.vatRate}%)`}
          valueGr={f.salePriceGr === null ? null : unitGrossGr(f.salePriceGr, f.vatRate)}
          disabled={fixed}
          description={fixed ? 'liczona ze stałej marży' : 'wpisana — netto przeliczy się samo'}
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          onChangeGr={(v) => {
            const net = v === null ? null : unitNetFromGrossGr(v, f.vatRate);
            set('salePriceGr', net);
            setMargin(marginPctOf(net, f.purchasePriceGr));
          }}
        />
      </SimpleGrid>
      {costVisible && (
        <Group gap="md" align="flex-end" wrap="wrap">
          <Switch
            label="Stała marża — cena sprzedaży liczona z ceny zakupu"
            description="Przy każdej zmianie ceny zakupu cena sprzedaży przeliczy się sama. Oferty już wydane się nie zmieniają."
            checked={fixed}
            onChange={(e) => {
              const on = e.currentTarget.checked;
              setFixed(on);
              if (on) {
                const m = margin ?? 25;
                setMargin(m);
                if (f.purchasePriceGr !== null) set('salePriceGr', priceFor(f.purchasePriceGr, m, true));
              }
            }}
            style={{ flex: '1 1 260px' }}
          />
          {fixed && (
            <Select
              label="Zaokrąglenie ceny"
              w={{ base: '100%', sm: 190 }}
              allowDeselect={false}
              value={f.priceRounding}
              data={Object.entries(PRICE_ROUNDINGS).map(([value, label]) => ({ value, label }))}
              onChange={(v) => {
                if (!v) return;
                const rounding = v as PriceRounding;
                setF((x) => ({ ...x, priceRounding: rounding, ...(x.purchasePriceGr !== null && margin !== null ? { salePriceGr: priceFor(x.purchasePriceGr, margin, true, rounding) ?? x.salePriceGr } : {}) }));
              }}
            />
          )}
        </Group>
      )}
      {f.kind === 'pakiet' && (
        <Stack gap={6}>
          <Text fw={600} size="sm">
            Skład pakietu
          </Text>
          {f.components.map((c, i) => (
            <Group key={i} wrap="nowrap">
              <Select
                data={componentOptions}
                value={c.itemId}
                searchable
                onChange={(v) => v && set('components', f.components.map((x, j) => (j === i ? { ...x, itemId: v } : x)))}
                style={{ flex: 1 }}
              />
              <NumberInput w={90} min={0.001} decimalScale={3} value={c.quantity} onChange={(v) => set('components', f.components.map((x, j) => (j === i ? { ...x, quantity: Number(v) || 1 } : x)))} />
              <ActionIcon variant="subtle" color="red" onClick={() => set('components', f.components.filter((_, j) => j !== i))} aria-label="Usuń składnik">
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
          <Group justify="space-between">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => componentOptions[0] && set('components', [...f.components, { itemId: componentOptions[0].value, quantity: 1 }])}
            >
              Dodaj składnik
            </Button>
            <Text size="xs" c="dimmed">
              Suma cen składników: <Money gr={componentsSum} />
            </Text>
          </Group>
        </Stack>
      )}
      <Group>
        <Switch label="Widoczna dla montażu (dokumentacja w aplikacji mobilnej)" checked={f.forInstallation} onChange={(e) => set('forInstallation', e.currentTarget.checked)} />
        <Switch label="Cennik serwisu (do wyboru przez serwisanta na miejscu)" checked={f.servicePrice} onChange={(e) => set('servicePrice', e.currentTarget.checked)} />
        <Switch label="Aktywna" checked={f.active} onChange={(e) => set('active', e.currentTarget.checked)} />
      </Group>
      {errors.length > 0 && (
        <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
          {errors.join('\n')}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          Anuluj
        </Button>
        <Button onClick={submit} loading={save.isPending}>
          Zapisz
        </Button>
      </Group>
    </Stack>
  );
}

function Attachments({ item, editable }: { item: CatalogItemDto; editable: boolean }) {
  const [kind, setKind] = useState<AttachmentKind>('karta');
  const [forInstallation, setForInstallation] = useState(true);
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', kind);
      form.append('title', file.name);
      form.append('forInstallation', String(forInstallation));
      const res = await fetch(`/api/catalog/${item.id}/attachments`, { method: 'POST', body: form, credentials: 'same-origin' });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { message?: string }).message ?? 'Nie udało się wgrać pliku');
      return res.json();
    },
    onSuccess: () => {
      notifyOk('Dodano załącznik');
      void qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: (fileId: string) => api(`/catalog/${item.id}/attachments/${fileId}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto załącznik');
      void qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: notifyError,
  });
  return (
    <Stack gap={6}>
      {item.attachments.map((a) => (
        <Group key={a.fileId} justify="space-between" wrap="nowrap">
          <Anchor href={`/api/catalog/${item.id}/attachments/${a.fileId}`} target="_blank" size="sm">
            <IconPaperclip size={12} /> {a.title || a.fileName}
          </Anchor>
          <Group gap={4} wrap="nowrap">
            <Badge size="xs" variant="light">
              {ATTACHMENT_KINDS[a.kind]}
            </Badge>
            {a.forInstallation && (
              <Badge size="xs" variant="light" color="teal">
                Dla montażu
              </Badge>
            )}
            {editable && (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                loading={remove.isPending && remove.variables === a.fileId}
                onClick={async () => {
                  if (await ask(`Usunąć załącznik „${a.title || a.fileName}”?${a.forInstallation ? ' Zniknie też z dokumentacji w aplikacji mobilnej.' : ''}`, { title: 'Usunąć załącznik?', confirm: 'Usuń', danger: true })) remove.mutate(a.fileId);
                }}
                aria-label="Usuń załącznik"
              >
                <IconTrash size={12} />
              </ActionIcon>
            )}
          </Group>
        </Group>
      ))}
      {item.attachments.length === 0 && <EmptyState compact icon={<IconPaperclip size={18} />} title="Brak załączników" />}
      {editable && (
        <Group mt="xs" wrap="wrap">
          <Select size="xs" w={{ base: '100%', sm: 190 }} data={Object.entries(ATTACHMENT_KINDS).map(([value, label]) => ({ value, label }))} value={kind} onChange={(v) => v && setKind(v as AttachmentKind)} allowDeselect={false} />
          <Checkbox size="xs" label="Dla montażu" checked={forInstallation} onChange={(e) => setForInstallation(e.currentTarget.checked)} />
          <FileButton onChange={(f) => f && upload.mutate(f)} accept="image/png,image/jpeg,image/webp,application/pdf">
            {(props) => (
              <Button {...props} size="xs" variant="light" loading={upload.isPending}>
                Dodaj plik (PDF, zdjęcie)
              </Button>
            )}
          </FileButton>
        </Group>
      )}
    </Stack>
  );
}

const SOURCE_LABELS: Record<BasePriceSource, string> = { zakup: 'ostatni zakup', reczna: 'ręczna (karta)', oferta: 'z kalkulacji oferty' };

/** Ceny bazowe (docs/14 §2.2): ręczna, ostatni rzeczywisty zakup i ta, która trafi do kalkulacji nowej oferty. */
export function BasePriceInfo({ item }: { item: CatalogItemDto }) {
  const bp = item.basePrice!;
  const wh = !!useMe().data?.features.magazyn;
  const staleDays = useWarehouseSettings(wh).data?.priceStaleDays;
  return (
    <Card withBorder padding="sm">
      <SimpleGrid cols={{ base: 1, sm: wh ? 3 : 2 }} spacing="xs">
        <div>
          <Text size="xs" c="dimmed">
            Ręczna cena bazowa
          </Text>
          <Money gr={bp.manualGr} />
          <Text size="xs" c="dimmed">
            {bp.manualGr === null ? 'brak' : `${bp.manualSource ? SOURCE_LABELS[bp.manualSource] : ''}${bp.manualAt ? ` · ${datePl(bp.manualAt)}` : ''}${bp.manualByName ? ` · ${bp.manualByName}` : ''}`}
          </Text>
        </div>
        {wh && (
        <div>
          <Text size="xs" c="dimmed">
            Ostatni rzeczywisty zakup (za {item.unit})
          </Text>
          <Money gr={bp.lastPurchaseGr === null ? null : Math.round(bp.lastPurchaseGr)} />
          <Text size="xs" c="dimmed">
            {bp.lastPurchaseGr === null ? 'brak przyjęć' : `${datePl(bp.lastPurchaseAt)} · firma ${bp.lastPurchaseCompanyCode ?? '—'}`}
          </Text>
        </div>
        )}
        <div>
          <Text size="xs" c="dimmed">
            Do kalkulacji nowej oferty
          </Text>
          <Group gap={6}>
            <Money gr={bp.effectiveGr} />
            {bp.stale && (
              <Badge size="xs" color="orange" variant="light" title={staleDays ? `Cena starsza niż ${staleDays} dni` : undefined}>
                Nieaktualna
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {bp.effectiveSource ? SOURCE_LABELS[bp.effectiveSource] : 'brak — cenę poda handlowiec w ofercie'}
          </Text>
        </div>
      </SimpleGrid>
    </Card>
  );
}

function PriceHistory({ item }: { item: CatalogItemDto }) {
  const wh = !!useMe().data?.features.magazyn;
  const q = useQuery({ queryKey: ['catalog', 'prices', item.id], queryFn: () => api<CatalogPriceDto[]>(`/catalog/${item.id}/prices`) });
  return (
    <QueryState isLoading={q.isLoading} error={q.error} lines={2}>
      {q.data?.length === 0 ? (
        <EmptyState compact icon={<IconHistory size={18} />} title="Brak historii cen" />
      ) : (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Od</Table.Th>
          <Table.Th ta="right">Sprzedaż</Table.Th>
          {item.costVisible && <Table.Th ta="right">{wh ? 'Cena bazowa' : 'Zakup'}</Table.Th>}
          <Table.Th>Kto</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {q.data?.map((p, i) => (
          <Table.Tr key={i}>
            <Table.Td>{formatDateTime(p.validFrom)}</Table.Td>
            <Table.Td ta="right">
              <Money gr={p.salePriceGr} />
            </Table.Td>
            {item.costVisible && (
              <Table.Td ta="right">
                <Money gr={p.purchasePriceGr} />
                {p.source !== 'reczna' && (
                  <Text size="xs" c="dimmed">
                    {SOURCE_LABELS[p.source]}
                  </Text>
                )}
              </Table.Td>
            )}
            <Table.Td>{p.changedByName ?? '—'}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
      )}
    </QueryState>
  );
}

export function CatalogPage({ me }: { me: MeDto }) {
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q, 300);
  const [kind, setKind] = useState<string | null>(null);
  const [trade, setTrade] = useState<string | null>(null);
  const [group, setGroup] = useState<string | null>(null);
  const [onlyActive, setOnlyActive] = useState(true);
  const [selected, setSelected] = useState<CatalogItemDto | 'new' | null>(null);
  const trades = useTrades();
  const groups = useCatalogGroups(me.features.magazyn);
  const params = new URLSearchParams();
  if (debounced.trim()) params.set('q', debounced.trim());
  if (kind) params.set('kind', kind);
  if (trade) params.set('trade', trade);
  if (group) params.set('groupId', group);
  if (onlyActive) params.set('active', 'true');
  const list = useQuery({ queryKey: ['catalog', params.toString()], queryFn: () => api<CatalogItemDto[]>(`/catalog?${params}`) });
  const all = useQuery({ queryKey: ['catalog', 'all'], queryFn: () => api<CatalogItemDto[]>('/catalog') });
  const editable = can(me, 'katalog.edycja');
  const current = selected && selected !== 'new' ? (list.data?.find((i) => i.id === selected.id) ?? selected) : null;

  return (
    <>
      <PageHeader
        title="Katalog"
        description="Urządzenia, materiały, usługi i pakiety — wspólne dla wszystkich firm. Cena w ofercie jest kopią ceny z chwili dodania pozycji: zmiana cennika nie zmienia wydanych ofert."
        actions={
          editable && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setSelected('new')}>
              Nowa pozycja katalogu
            </Button>
          )
        }
      />
      <Group mb="md" wrap="wrap">
        <TextInput placeholder={me.features.magazyn ? 'Szukaj: nazwa, indeks, EAN, producent, model…' : 'Szukaj: nazwa, indeks, producent, model…'} leftSection={<IconSearch size={16} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={{ base: '100%', sm: 320 }} />
        <Select placeholder="Rodzaj" clearable data={Object.entries(CATALOG_KINDS).map(([value, label]) => ({ value, label }))} value={kind} onChange={setKind} w={{ base: '100%', sm: 160 }} />
        {me.features.magazyn && <Select placeholder="Grupa" clearable searchable data={(groups.data ?? []).map((g) => ({ value: g.id, label: g.name }))} value={group} onChange={setGroup} w={{ base: '100%', sm: 200 }} />}
        <Select placeholder="Branża" clearable data={(trades.data ?? []).map((t) => ({ value: t.code, label: t.name }))} value={trade} onChange={setTrade} w={{ base: '100%', sm: 220 }} />
        <Switch label="Tylko aktywne" checked={onlyActive} onChange={(e) => setOnlyActive(e.currentTarget.checked)} />
      </Group>
      <Card padding={0}>
      <QueryState isLoading={list.isLoading} error={list.error}>
        {list.data?.length === 0 ? (
          <EmptyState
            icon={<IconPackage size={24} />}
            title="Brak pozycji"
            hint={debounced || kind || trade ? 'Nic nie pasuje do wyszukiwania i filtrów.' : editable ? 'Dodaj pierwszą pozycję przyciskiem „Nowa pozycja katalogu”.' : undefined}
          />
        ) : (
        <Table.ScrollContainer minWidth={820}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Pozycja</Table.Th>
                <Table.Th>Rodzaj</Table.Th>
                <Table.Th>Jedn. / VAT</Table.Th>
                <Table.Th ta="right">Cena netto</Table.Th>
                <Table.Th ta="right">Cena brutto</Table.Th>
                {list.data?.[0]?.costVisible && <Table.Th ta="right">Zakup</Table.Th>}
                <Table.Th ta="right">Pliki</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.data?.map((i) => (
                <Table.Tr key={i.id} className="row-link" style={{ opacity: i.active ? 1 : 0.5 }} onClick={() => setSelected(i)}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Group gap={2} wrap="nowrap">
                        {i.tradeCodes.map((t) => (
                          <TradeBadge key={t} code={t} />
                        ))}
                      </Group>
                      <div>
                        <Text size="sm" fw={600}>
                          {i.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {[i.code, i.groupName, i.manufacturer, i.model].filter(Boolean).join(' · ')}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={i.kind === 'pakiet' ? 'grape' : 'gray'} size="sm">
                      {CATALOG_KINDS[i.kind]}
                    </Badge>
                    {i.servicePrice && (
                      <Text size="xs" c="dimmed">
                        cennik serwisu
                      </Text>
                    )}
                    {i.hideInOffers && me.features.magazyn && (
                      <Text size="xs" c="dimmed">
                        ukryty w ofertach
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {i.unit} · {pctPl(i.vatRate)}
                  </Table.Td>
                  <Table.Td ta="right">{i.pricesVisible ? <Money gr={i.salePriceGr} /> : <Text c="dimmed" size="xs">ukryta</Text>}</Table.Td>
                  <Table.Td ta="right">{i.pricesVisible ? <Money gr={i.salePriceGr === null ? null : unitGrossGr(i.salePriceGr, i.vatRate)} /> : <Text c="dimmed" size="xs">ukryta</Text>}</Table.Td>
                  {i.costVisible && (
                    <Table.Td ta="right">
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Money gr={i.basePrice?.effectiveGr ?? i.purchasePriceGr} />
                        {i.basePrice?.stale && (
                          <Badge size="xs" color="orange" variant="light">
                            Nieaktualna
                          </Badge>
                        )}
                      </Group>
                      {i.basePrice?.effectiveSource === 'zakup' && (
                        <Text size="xs" c="dimmed">
                          ostatni zakup {datePl(i.basePrice.effectiveAt)}
                        </Text>
                      )}
                      {marginPctOf(i.salePriceGr, i.basePrice?.effectiveGr ?? i.purchasePriceGr) !== null && (
                        <Text size="xs" c="dimmed">
                          marża {pctPl(marginPctOf(i.salePriceGr, i.basePrice?.effectiveGr ?? i.purchasePriceGr)!)}
                          {i.targetMarginPct !== null && (
                            <Badge size="xs" variant="light" ml={4}>
                              stała
                            </Badge>
                          )}
                        </Text>
                      )}
                    </Table.Td>
                  )}
                  <Table.Td ta="right" className="tabular">
                    {i.attachments.length || '—'}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        )}
        {!!list.data?.length && !list.data[0]?.pricesVisible && (
          <Text size="xs" c="dimmed" px="md" py="sm">
            Ceny są ukryte — Twoja rola nie ma uprawnienia do cen.
          </Text>
        )}
      </QueryState>
      </Card>
      <Drawer
        opened={selected !== null}
        onClose={() => setSelected(null)}
        position="right"
        size="xl"
        title={selected === 'new' ? 'Nowa pozycja katalogu' : current?.name}
      >
        {selected === 'new' && <ItemEditor item={null} all={all.data ?? []} onDone={(i) => setSelected(i)} onCancel={() => setSelected(null)} />}
        {current && (
          <Stack>
            {editable ? (
              <ItemEditor key={current.id + current.updatedAt} item={current} all={all.data ?? []} onDone={(i) => setSelected(i)} onCancel={() => setSelected(null)} />
            ) : (
              <Stack gap={4}>
                <Text size="sm">{current.description || 'Brak opisu.'}</Text>
                {current.components.length > 0 && <Text size="sm">Skład: {current.components.map((c) => `${c.name} × ${c.quantity}`).join(', ')}</Text>}
              </Stack>
            )}
            <Divider label="Dokumentacja i zdjęcia" />
            <Attachments item={current} editable={editable} />
            {current.pricesVisible && (
              <>
                <Divider label="Historia cennika" />
                <PriceHistory item={current} />
              </>
            )}
          </Stack>
        )}
      </Drawer>
    </>
  );
}
